import React from "react"
import {
  LayoutChangeEvent,
  Text,
  TextStyle,
  useWindowDimensions,
  View,
  ViewStyle,
} from "react-native"

import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated"

import {
  ProgressStep,
  ProgressStepName,
  StepStatus,
  useSidekickProgress,
} from "src/context/SidekickProgressContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { ThemedStyle } from "src/theme"
import { ProgressStepIcon } from "./ProgressStepIcon"

type AllStepName = "listening" | ProgressStepName

const STEP_LABELS: Record<AllStepName, string> = {
  listening: "Listening",
  researching: "Researching",
  thinking: "Thinking",
  authoring: "Authoring",
}

interface SidekickProgressProps {
  variant: "chat" | "overlay"
}

export const SidekickProgress = ({ variant }: SidekickProgressProps) => {
  const { isProgressVisible, isOverlayVisible, isListeningPhase, showListeningStep, steps } =
    useSidekickProgress()
  const { themed } = useAppTheme()
  const { fontScale } = useWindowDimensions()

  const isOverlay = variant === "overlay"

  // Overlay uses isOverlayVisible which stays true across the listening→processing transition
  // with no render gap. Chat only shows during processing steps.
  const isVisible = isOverlay ? isOverlayVisible : isProgressVisible

  // Tracks the natural rendered height of the card so we know the target for expansion.
  const naturalHeight = useSharedValue(0)
  // Height of the clipping wrapper — animated for chat, set instantly for overlay.
  const wrapperHeight = useSharedValue(0)
  // Card opacity, animated for both variants.
  const cardOpacity = useSharedValue(isVisible ? 1 : 0)

  React.useEffect(() => {
    if (isVisible) {
      if (isOverlay) {
        // Overlay appear: set height instantly (no vertical animation), then fade in.
        if (naturalHeight.value > 0) {
          wrapperHeight.value = naturalHeight.value
        }
      } else {
        // Chat appear: animate height and opacity together.
        if (naturalHeight.value > 0) {
          wrapperHeight.value = withTiming(naturalHeight.value, {
            duration: 300,
            easing: Easing.out(Easing.cubic),
          })
        }
      }
      cardOpacity.value = withTiming(1, { duration: 300, easing: Easing.out(Easing.cubic) })
    } else {
      if (isOverlay) {
        // Overlay disappear: fade out only; collapse height invisibly after the fade completes
        // so the surrounding layout doesn't shift while the card is still visible.
        cardOpacity.value = withTiming(0, { duration: 200 }, (finished) => {
          if (finished) {
            wrapperHeight.value = 0
          }
        })
      } else {
        // Chat disappear: animate height and opacity together.
        wrapperHeight.value = withTiming(0, { duration: 250, easing: Easing.in(Easing.cubic) })
        cardOpacity.value = withTiming(0, { duration: 200 })
      }
    }
  }, [isVisible, isOverlay, wrapperHeight, naturalHeight, cardOpacity])

  // Chat: overflow hidden so the card is clipped during the height animation.
  // Overlay: overflow visible so the card's elevation shadow isn't clipped by the wrapper.
  const chatWrapperStyle = useAnimatedStyle(() => ({
    height: wrapperHeight.value,
    overflow: "hidden",
  }))
  const overlayWrapperStyle = useAnimatedStyle(() => ({
    height: wrapperHeight.value,
    overflow: "visible",
  }))
  const wrapperStyle = isOverlay ? overlayWrapperStyle : chatWrapperStyle

  const cardStyle = useAnimatedStyle(() => ({
    opacity: cardOpacity.value,
  }))

  const handleLayout = React.useCallback(
    (event: LayoutChangeEvent) => {
      const cardHeight = event.nativeEvent.layout.height
      if (cardHeight <= 0) return
      // onLayout excludes margins. $card has marginVertical: 8, so the card is offset
      // 8px from the top of the wrapper — add that margin back so the wrapper is tall
      // enough to fully contain the card (border, corners, and all).
      const CARD_MARGIN_VERTICAL = 8
      const h = cardHeight + CARD_MARGIN_VERTICAL
      naturalHeight.value = h
      if (isVisible) {
        if (isOverlay) {
          wrapperHeight.value = h // instant for overlay
        } else {
          wrapperHeight.value = withTiming(h, { duration: 300, easing: Easing.out(Easing.cubic) })
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isVisible, isOverlay],
  )

  // For the overlay, prepend a "Listening" step before the processing steps, but only when
  // the user actually initiated a voice interaction. Skip it for typed messages and
  // server-initiated responses like the welcome message.
  const displaySteps: Array<{ name: AllStepName; status: StepStatus }> = isOverlay
    ? [
        ...(showListeningStep
          ? [
              {
                name: "listening" as AllStepName,
                status: (isListeningPhase ? "in_progress" : "complete") as StepStatus,
              },
            ]
          : []),
        ...(steps as ProgressStep[]),
      ]
    : steps

  return (
    <Animated.View style={wrapperStyle}>
      <Animated.View
        style={[themed($card), isOverlay && themed($overlayCard), cardStyle]}
        onLayout={handleLayout}
      >
        {displaySteps.map((step) => (
          <View key={step.name} style={$stepRow}>
            <ProgressStepIcon status={step.status} />
            <Text style={[themed($stepLabel), { lineHeight: Math.ceil(14 * fontScale * 1.35) }]}>
              {STEP_LABELS[step.name]}
            </Text>
          </View>
        ))}
      </Animated.View>
    </Animated.View>
  )
}

const $card: ThemedStyle<ViewStyle> = ({ colors }) => ({
  backgroundColor: colors.background,
  borderRadius: 16,
  paddingHorizontal: 16,
  paddingVertical: 12,
  gap: 10,
  marginHorizontal: 16,
  marginVertical: 8,
  alignSelf: "flex-start",
  minWidth: 180,
})

const $overlayCard: ThemedStyle<ViewStyle> = ({ colors }) => ({
  marginHorizontal: 0,
  marginVertical: 0,
  minWidth: 180,
  shadowColor: colors.shadow,
  shadowOffset: { width: 0, height: 4 },
  shadowOpacity: 0.3,
  shadowRadius: 8,
  elevation: 8,
})

const $stepRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 10,
}

const $stepLabel: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  color: colors.text,
  fontFamily: typography.fonts.figtree.normal,
  fontSize: 14,
})
